import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import PermissionMatrix from "./PermissionMatrix";
import { addRole, editRole, getRoleById } from "../../../service/api";
import { PERMISSION_MODULES } from "../../../utils/rbac";
import { decryptString } from "../../../config/validation";

const RoleForm = ({ onBack, editId = null }) => {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editId) {
      fetchRole();
    } else {
      // Initialize with all modules set to false
      const defaultPerms = PERMISSION_MODULES.map((mod) => ({
        module: mod.key,
        view: false,
        create: false,
        update: false,
        delete: false,
      }));
      setPermissions(defaultPerms);
    }
  }, [editId]);

  const fetchRole = async () => {
    setFetchLoading(true);
    try {
      const res = await getRoleById(editId);
      if (res?.status && res?.data) {
        setName(res.data.name || "");
        setDescription(res.data.description || "");
        setIsActive(res.data.isActive !== false);
        if (res.data.permissions && res.data.permissions.length > 0) {
          // Merge fetched permissions with all modules (in case new modules were added)
          const fetchedPerms = res.data.permissions;
          const mergedPerms = PERMISSION_MODULES.map((mod) => {
            const existing = fetchedPerms.find((p) => p.module === mod.key);
            return (
              existing || {
                module: mod.key,
                view: false,
                create: false,
                update: false,
                delete: false,
              }
            );
          });
          setPermissions(mergedPerms);
        }
      }
    } catch (e) {
      toast.error("Failed to fetch role details", { toastId: 'role-form-fetch-error' });
    }
    setFetchLoading(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = "Role name is required";
    } else if (name.trim().length < 2 || name.trim().length > 100) {
      newErrors.name = "Role name must be between 2-100 characters";
    }

    // Check at least one permission is enabled
    const hasAnyPermission = permissions.some(
      (p) => p.view || p.create || p.update || p.delete,
    );
    if (!hasAnyPermission) {
      newErrors.permissions = "At least one permission must be enabled";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      // Only send permissions that have at least one action enabled
      const activePermissions = permissions.filter(
        (p) => p.view || p.create || p.update || p.delete,
      );

      const plainData = {
        name: name.trim(),
        description: description.trim(),
        isActive,
        permissions: activePermissions,
      };

      const encryptedData = await decryptString(plainData);
      const payload = { encrypt: encryptedData };

      let res;
      if (editId) {
        res = await editRole(editId, payload);
      } else {
        res = await addRole(payload);
      }

      if (res?.status) {
        toast.success(res?.message || (editId ? "Role updated" : "Role created"), { toastId: 'role-form-save-success' });
        onBack();
      } else {
        toast.error(res?.message || "Operation failed", { toastId: 'role-form-save-error' });
      }
    } catch (e) {
      toast.error(
        e?.data?.message || "Something went wrong", { toastId: 'role-form-save-error' },
      );
    }
    setLoading(false);
  };

  if (fetchLoading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="content container-fluid">
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FaArrowLeft
                onClick={onBack}
                style={{ cursor: "pointer", fontSize: "18px" }}
              />
              {editId ? "Edit Role" : "Add Role"}
            </h3>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel style={{ marginBottom: "8px", fontWeight: "500" }}>
                      Role Name *
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter role name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel style={{ marginBottom: "8px", fontWeight: "500" }}>
                      Description
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row" style={{ marginBottom: "16px" }}>
                <div className="col-md-6">
                  <div className="form-group">
                    <InputLabel style={{ marginBottom: "8px", fontWeight: "500" }}>
                      Status
                    </InputLabel>
                    <select
                      className="form-control"
                      value={isActive ? "active" : "inactive"}
                      onChange={(e) => setIsActive(e.target.value === "active")}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <h5 style={{ marginBottom: "12px" }}>Permissions</h5>
                {errors.permissions && (
                  <p style={{ color: "red", fontSize: "13px", marginBottom: "8px" }}>
                    {errors.permissions}
                  </p>
                )}
                <PermissionMatrix
                  value={permissions}
                  onChange={setPermissions}
                />
              </div>

              <div style={{ marginTop: "24px" }}>
                <button
                  className="btn btn-primary me-2"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : editId ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </button>
                <button className="btn btn-secondary" onClick={onBack}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleForm;
